import * as React from 'react';
import cn from 'classnames';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router';
import { Link, Redirect } from 'react-router-dom';
import { Coords } from 'google-map-react';
import { SearchIcon, RecentItemsIcon, HotelIcon } from '@cvent/carina/components/Icon';
import { Button } from '@cvent/carina/components/Button';
import { IconTile } from './IconTile';
import GooglePlacesAutocomplete from '../hotels-search/Autocomplete';
import { HOTELS_SRP } from '../../urls/hotels-urls';
import { SRP_BASE } from '../../urls';
import { logEvent, AnalyticsEvent } from '@weddingspot/ws-analytics';
import { CSNGeographicPointFilter, CSNBoundingBox } from '@weddingspot/ws-api-client';

const styles = require('./index.module.scss');

const validateRoomsCount = (count: number | undefined) => {
    if (count !== undefined) {
        if (count <= 0) {
            return 'Please specify a number > 0';
        }
        if (count > 500) {
            return 'Please sepcify a number <= 500';
        }
    }
    return;
};

const LandingPage = () => {
    const history = useHistory();
    const [query, setQuery] = React.useState<string>();
    const [geoFilter, setGeoFilter] = React.useState<CSNGeographicPointFilter>();
    const [boundingBox, setBoundingBox] = React.useState<CSNBoundingBox>();
    const [roomsCount, setRoomsCount] = React.useState<number>();
    const [canSubmit, setCanSubmit] = React.useState<boolean>(false);
    const [autocompliteActive, setAutocompliteActive] = React.useState(false);
    const roomsCountError = validateRoomsCount(roomsCount);

    React.useEffect(() => logEvent(AnalyticsEvent.EVTNM_VISITED_HOTELS_HOME, {}), []);

    React.useEffect(() => {
        setCanSubmit(!!query && !!geoFilter && !roomsCountError);
    }, [query, geoFilter, roomsCount]);

    const onPlaceSelected = (address: string, geoFilter: CSNGeographicPointFilter, boundingBox: CSNBoundingBox) => {
        setQuery(address);
        setGeoFilter(geoFilter);
        setBoundingBox(boundingBox);
        logEvent(AnalyticsEvent.EVTNM_CLICKED_HOTEL_LOCATIONS, { source: 'hotel blocks homepage', location: address });
    };

    return (
        <div className={styles.page}>
            <Helmet
                meta={[
                    {
                        name: 'description',
                        content:
                            'Get the best price on your hotel block for your wedding guests through our hotel reservation system that specializes in servicing wedding events. Learn more today!',
                    },
                ]}
            >
                <title>Hotel Room Blocks for Weddings | Wedding Spot</title>
            </Helmet>
            <div className={styles.heroSectionContainer}>
                {/* Hero section */}
                <div className={styles.heroImageContainer}>
                    <div className={styles.heroContentContainer}>
                        <div className={styles.heroTitleContainer}>
                            <h1 className={styles.heroTitle}>An Easier Way To Book Hotel Room Blocks</h1>
                            <div className={styles.heroSubtitle}>
                                We'll help you find the best hotel rooms for your guests at the best prices
                            </div>
                        </div>
                        <div className={styles.heroFormContainer}>
                            <div className={styles.heroFormContent}>
                                {/* Search form */}
                                <div className={cn(styles.heroSearchContainer)}>
                                    <div className={styles.inputLabel}>
                                        <span className={styles.requiredFieldIndicator}>*</span>Venue Location
                                    </div>
                                    <GooglePlacesAutocomplete
                                        isActive={autocompliteActive}
                                        onChangeActive={(isActive) => setAutocompliteActive(isActive)}
                                        onPlaceSelected={onPlaceSelected}
                                        onClear={() => {}}
                                    />
                                    <div className={styles.helpText}>
                                        Don't have a venue yet?{' '}
                                        {
                                            <Link className={styles.weddingSRPLink} to={SRP_BASE} target='_blank'>
                                                Let's help
                                            </Link>
                                        }
                                    </div>
                                </div>

                                <div className={styles.heroRoomsContainer}>
                                    <div className={styles.inputLabel}>Guest Rooms</div>
                                    <input
                                        type='number'
                                        placeholder='Add'
                                        min='1'
                                        max='10000'
                                        onChange={(e) => setRoomsCount(parseInt(e.target.value, 10))}
                                    />
                                    {!!roomsCountError && <div className={styles.rcError}>{roomsCountError}</div>}
                                </div>
                                <Button
                                    text='Search hotels'
                                    disabled={!canSubmit}
                                    appearance='filled'
                                    onClick={() => {
                                        history.push(HOTELS_SRP, {
                                            q: query,
                                            f: geoFilter,
                                            b: boundingBox,
                                            r_min: roomsCount,
                                        });
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div>
                {/* Rest */}
                <div className={styles.stepsContainer}>
                    <h2 className={styles.stepsTitle}>Find a Wedding Venue: How It Works</h2>
                    <div className={styles.stepsTilesContainer}>
                        <IconTile
                            title1='Step 1'
                            icon={<SearchIcon color='#881155' size='l' />}
                            title2='Search Hotels'
                            subtitle='Tell us what you want in a hotel for your guests.'
                        />
                        <IconTile
                            title1='Step 2'
                            icon={<RecentItemsIcon color='#881155' size='l' />}
                            title2='Request Proposal'
                            subtitle="Send a proposal to get a quote from the hotel that will fit your wedding's needs."
                        />
                        <IconTile
                            title1='Step 3'
                            icon={<HotelIcon color='#881155' size='l' />}
                            title2='Select Hotel'
                            subtitle='Choose the hotel that best fits your needs.'
                        />
                    </div>
                </div>
                <div className={styles.faqSection}>
                    {/* FAQ */}
                    <div className={styles.faqTitle}>Frequently Asked Questions</div>
                    <div className={styles.faqContainer}>
                        <div className={styles.faqQA}>
                            <div className={styles.faqQuestion}>What is a hotel room block?</div>
                            <div className={styles.faqAnswer}>
                                A hotel room block is a reservation for a group of hotel rooms. You can receive the best rates by reserving
                                a room block.
                            </div>
                        </div>
                        <div className={styles.faqQA}>
                            <div className={styles.faqQuestion}>How can I search for a hotel room block?</div>
                            <div className={styles.faqAnswer}>
                                Enter the name of your wedding venue or venue address to explore the best hotels closest to your venue.
                            </div>
                        </div>
                        <div className={styles.faqQA}>
                            <div className={styles.faqQuestion}>How does this work?</div>
                            <div className={styles.faqAnswer}>
                                You select the hotels you like and send them a request for a proposal. You will receive proposals from
                                hotels with room block rates. Negotiate the best rate that works for you and ensure that all your guests are
                                able to stay in one place.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LandingPage;
